import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import type {
  RecorrenciaOS,
  FrequenciaRecorrencia,
  ItemRecorrencia,
} from '@/types/recorrencia';
import type { ServicoOS, EnderecoOS } from '@/types/ordemServico';
import { proximaDataInicial } from '@/lib/recorrencia-datas';
import { isPaidPlan } from '@/lib/planos';

const RECORRENCIA_SELECT = `
  *,
  cliente:clientes_potenciais!ordens_servico_recorrentes_cliente_id_fkey(id, nome, telefone),
  prestador:prestadores_servico!ordens_servico_recorrentes_prestador_id_fkey(
    id,
    profile:profiles!prestadores_servico_profile_id_fkey(id, nome)
  )
`;

export async function listRecorrencias(): Promise<RecorrenciaOS[]> {
  const { data, error } = await supabase
    .from('ordens_servico_recorrentes')
    .select(RECORRENCIA_SELECT)
    .order('criado_em', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as RecorrenciaOS[];
}

export interface CriarRecorrenciaInput {
  orgId: string;
  cliente_id: string;
  prestador_id?: string | null;
  responsavel_id?: string | null;
  frequencia: FrequenciaRecorrencia;
  data_agendada: string; // the first OS's scheduled datetime — the anchor
  servicos: ServicoOS[];
  itens: ItemRecorrencia[];
  endereco: EnderecoOS;
  valor_total: number;
  observacoes?: string;
  duracao_estimada_minutos?: number | null;
  primeira_os_id?: string; // if set, this OS is linked as occurrence #1
}

export async function criarRecorrencia(
  input: CriarRecorrenciaInput,
): Promise<{ id: string }> {
  // Defensive plan gate (defense-in-depth: the OS-form switch is also disabled
  // on Starter, and the RPC skips Starter orgs). Recurrences are a paid feature.
  const { data: org, error: orgError } = await supabase
    .from('organizations')
    .select('plano')
    .eq('id', input.orgId)
    .single();
  if (orgError) throw orgError;
  if (!isPaidPlan(org.plano)) {
    throw new Error('Recorrências estão disponíveis apenas em planos pagos.');
  }

  const { data, error } = await supabase
    .from('ordens_servico_recorrentes')
    .insert([{
      organization_id: input.orgId,
      cliente_id: input.cliente_id,
      prestador_id: input.prestador_id ?? null,
      responsavel_id: input.responsavel_id ?? null,
      frequencia: input.frequencia,
      proxima_data: proximaDataInicial(input.data_agendada, input.frequencia),
      ativa: true,
      servicos: input.servicos as unknown as Json,
      itens: input.itens as unknown as Json,
      endereco: input.endereco as unknown as Json,
      valor_total: input.valor_total,
      observacoes: input.observacoes ?? '',
      duracao_estimada_minutos: input.duracao_estimada_minutos ?? null,
    }])
    .select('id')
    .single();
  if (error) throw error;

  // Link the first OS (occurrence #1) so it shows the badge and is traceable.
  // Note: not atomic with the insert above — if this link fails the recurrence
  // row still exists and works (it just generates from occurrence #2 onward,
  // and occurrence #1's badge won't show). Acceptable: this is an update by PK
  // on a row the caller just created, so failure is rare.
  if (input.primeira_os_id) {
    const { error: linkError } = await supabase
      .from('ordens_servico')
      .update({ recorrencia_id: data.id })
      .eq('id', input.primeira_os_id);
    if (linkError) throw linkError;
  }

  return data;
}

export interface AtualizarRecorrenciaInput {
  frequencia?: FrequenciaRecorrencia;
  proxima_data?: string;
  prestador_id?: string | null;
  servicos?: ServicoOS[];
  itens?: ItemRecorrencia[];
  endereco?: EnderecoOS;
  valor_total?: number;
  observacoes?: string;
  duracao_estimada_minutos?: number | null;
}

export async function atualizarRecorrencia(
  id: string,
  dados: AtualizarRecorrenciaInput,
): Promise<void> {
  const updateData: Record<string, unknown> = { atualizado_em: new Date().toISOString() };
  if (dados.frequencia !== undefined) updateData.frequencia = dados.frequencia;
  if (dados.proxima_data !== undefined) updateData.proxima_data = dados.proxima_data;
  if (dados.prestador_id !== undefined) updateData.prestador_id = dados.prestador_id;
  if (dados.servicos !== undefined) updateData.servicos = dados.servicos as unknown as Json;
  if (dados.itens !== undefined) updateData.itens = dados.itens as unknown as Json;
  if (dados.endereco !== undefined) updateData.endereco = dados.endereco as unknown as Json;
  if (dados.valor_total !== undefined) updateData.valor_total = dados.valor_total;
  if (dados.observacoes !== undefined) updateData.observacoes = dados.observacoes;
  if (dados.duracao_estimada_minutos !== undefined) {
    updateData.duracao_estimada_minutos = dados.duracao_estimada_minutos;
  }

  const { error } = await supabase
    .from('ordens_servico_recorrentes')
    .update(updateData)
    .eq('id', id);
  if (error) throw error;
}

export async function pausarRecorrencia(id: string, ativa: boolean): Promise<void> {
  const { error } = await supabase
    .from('ordens_servico_recorrentes')
    .update({ ativa, atualizado_em: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export interface TerminarRecorrenciaInput {
  id: string;
  orgId: string;
}

// Hard-deletes a recurrence with an auditoria_exclusoes snapshot (CLAUDE.md:
// never delete data without an audit record). Already-generated OS survive —
// their recorrencia_id becomes null via the FK's ON DELETE SET NULL.
export async function terminarRecorrencia(input: TerminarRecorrenciaInput): Promise<void> {
  const { data: rec, error: fetchError } = await supabase
    .from('ordens_servico_recorrentes')
    .select('cliente_id, prestador_id, frequencia, valor_total, servicos, endereco')
    .eq('id', input.id)
    .single();
  if (fetchError) throw fetchError;

  const { data: profileId } = await supabase.rpc('get_my_profile_id');
  const { data: profile } = await supabase
    .from('profiles')
    .select('nome')
    .eq('id', profileId as string)
    .single();

  await supabase.from('auditoria_exclusoes').insert({
    organization_id: input.orgId,
    entidade: 'ordens_servico_recorrentes',
    entidade_id: input.id,
    dados_excluidos: {
      cliente_id: rec.cliente_id,
      prestador_id: rec.prestador_id,
      frequencia: rec.frequencia,
      valor_total: rec.valor_total,
      servicos: rec.servicos,
      endereco: rec.endereco,
    } as unknown as Json,
    usuario_id: profileId as string,
    usuario_nome: profile?.nome || 'Usuário',
    motivo: 'Recorrência terminada',
  });

  const { error } = await supabase
    .from('ordens_servico_recorrentes')
    .delete()
    .eq('id', input.id);
  if (error) throw error;
}
